import React, { FC, memo, useEffect, useMemo, useState } from 'react'

import { useSetFocus, FocusNode, useFocusNodeById } from '@please/lrud'
import { Element, animateScroll } from 'react-scroll'

import { getSuggestions, getData } from '@/actions/search'
import { Keyboard } from '@/components/Keyboard'
import { SearchLinesGroup } from '@/components/LinesGroup/SearchLinesGroup'
import { orientation } from '@/constants/focus'
import { scrollOptions } from '@/constants/scrollers'
import { SearchHeader } from '@/containers/Search/SearchHeader'
import { ScrollContainer } from '@/containers/styles'
import { useCustomFocus } from '@/hooks/useCustomFocus'
import { useCommonTranslation } from '@/i18n/useCommonTranslation'
import { searchStoreInitialData } from '@/store/searchStore'
import useStore from '@/store/useStore'
import { SearchQuery } from '@/types/servicesTypes/searchTypes'

import {
  pageFocusId,
  linesFocusId,
  controlsFocusId,
  linesGroupFocusId,
  sliderIds,
} from './constants'
import { SearchInput } from './SearchInput'
import {
  PageContainer,
  SearchContainer,
  SearchControls,
  SearchInputWrapper,
  SearchControlsItem,
  SuggestionsTitle,
} from './styles'
import { Suggestions } from './Suggestions'

const SearchRow: FC<{ isSearchTab?: boolean }> = ({ isSearchTab }) => {
  const {
    data,
    loading,
    error,
    setSearchValue,
    setSuggestions,
    setEvents,
    setPages,
  } = useStore((state) => state.search)

  const focusIdPrefix = isSearchTab ? 'tab_' : ''

  const [shouldComplete, setShouldComplete] = useState(true)
  const [dataRequested, setDataRequested] = useState(false)

  const { t } = useCommonTranslation()
  const setFocus = useSetFocus()
  const controlsFocusNode = useFocusNodeById(focusIdPrefix + controlsFocusId)
  const { defaultFocusChild, onMove, setDefaultFocusChild } = useCustomFocus({
    focusId: focusIdPrefix + pageFocusId,
    scrollOptions: scrollOptions.Search,
  })

  const { suggestions, searchValue } = data
  const isLoading = loading.events || loading.pages
  const isError = error.events && error.pages

  const linesData = useMemo(
    () => [
      {
        name: 'Events',
        data: data?.events.items,
        next: data?.events.next,
        sliderId: data?.events.items.length
          ? data.searchValue
          : sliderIds.events,
        searchResultType: 'event' as SearchQuery,
        isSearchListType: true,
      },
      {
        name: 'Pages',
        data: data?.pages.items,
        next: data?.pages.next,
        sliderId: data?.pages.items.length ? data.searchValue : sliderIds.pages,
        searchResultType: 'page' as SearchQuery,
        isSearchListType: true,
      },
    ],
    [data?.events.items, data?.pages.items],
  )

  useEffect(() => {
    !isSearchTab && setFocus(focusIdPrefix + pageFocusId)
  }, [])

  useEffect(() => {
    const hasData = data.pages.items.length || data.events.items.length

    if (!isLoading && dataRequested && hasData) {
      setFocus(focusIdPrefix + linesFocusId)
      setDefaultFocusChild(1)
      animateScroll.scrollToBottom({
        containerId: scrollOptions.Search.containerId,
        smooth: false,
        duration: 0,
      })
    }
  }, [isLoading, dataRequested])

  useEffect(() => {
    if (!searchValue.length) {
      return
    }
    void getSuggestions(searchValue)
  }, [searchValue])

  const onSearch = (value: string) => {
    if (!value) {
      return
    }

    ;['event', 'page'].forEach((item) => {
      void getData(value, item as SearchQuery)
    })
    setShouldComplete(false)
    setDataRequested(true)
  }

  const onSelectedSuggestion = (value: string) => {
    setSearchValue(value)
    onSearch(value)
  }

  const onKeySubmit = (char: string) => {
    if (!searchValue.length && char === 'Space') {
      return
    }

    if (char === 'Backspace') {
      setSearchValue(searchValue.slice(0, -1))
      return
    }
    const newChar = char === 'Space' ? ' ' : char.toLowerCase()
    setSearchValue(searchValue + newChar)
    setShouldComplete(true)
  }

  const onClear = () => {
    setSearchValue('')
    setSuggestions([])
    setEvents(searchStoreInitialData)
    setPages(searchStoreInitialData)
    setDataRequested(false)
  }

  const footerPanelButtonsConfig = [
    { text: 'Clear', onClick: onClear },
    { text: 'Search', onClick: () => onSearch(searchValue) },
  ]

  const handleMove = (e: MoveEvent) => {
    const hasData = data.pages.items.length || data.events.items.length
    if (e.nextChildIndex === 1 && !hasData) {
      setFocus(focusIdPrefix + pageFocusId)
      return
    }
    onMove(e)
  }

  const setControlsFocus = () => {
    !controlsFocusNode?.isFocused && setFocus(focusIdPrefix + controlsFocusId)
  }

  const renderControls = () => {
    const hasData = data.pages.items.length || data.events.items.length
    const suggestionsData = hasData ? suggestions.slice(0, 4) : suggestions
    return (
      <SearchControls
        focusId={focusIdPrefix + controlsFocusId}
        elementType="section"
        defaultFocusChild={1}
      >
        <SearchControlsItem>
          {!!suggestionsData.length && (
            <SuggestionsTitle>{t('search.suggestions')}</SuggestionsTitle>
          )}
          <Suggestions
            focusIdPrefix={focusIdPrefix}
            items={suggestionsData}
            onSelect={onSelectedSuggestion}
          />
        </SearchControlsItem>
        <SearchControlsItem>
          <Keyboard
            focusIdPrefix={focusIdPrefix}
            onSelect={onKeySubmit}
            bottomPanelButtonsConfig={footerPanelButtonsConfig}
          />
        </SearchControlsItem>
      </SearchControls>
    )
  }

  const renderLines = () => (
    <FocusNode focusId={focusIdPrefix + linesFocusId}>
      <SearchLinesGroup
        linesData={linesData}
        scrollOptions={scrollOptions.Search}
        loading={isLoading}
        requestAction={() => onSearch(searchValue)}
        error={isError}
        dataRequested={dataRequested}
        focusIdPrefix={focusIdPrefix}
      />
    </FocusNode>
  )

  return (
    <PageContainer
      focusId={focusIdPrefix + pageFocusId}
      orientation={orientation.VERTICAL}
      className={isSearchTab ? '' : 'content-wrapper'}
      defaultFocusChild={defaultFocusChild}
      onMove={handleMove}
      $isSearchTab={isSearchTab}
    >
      {!isSearchTab && <SearchHeader />}
      <SearchContainer>
        <SearchInputWrapper onClick={setControlsFocus}>
          <SearchInput
            value={searchValue}
            placeholder={t('search.input.placeholder')}
            suggestion={suggestions && suggestions?.[0]}
            shouldComplete={shouldComplete}
          />
        </SearchInputWrapper>
        <ScrollContainer
          id={scrollOptions.Search.containerId}
          height={scrollOptions.Search.height}
          $withMargin
        >
          <Element name={focusIdPrefix + controlsFocusId}>
            {renderControls()}
          </Element>
          <Element name={focusIdPrefix + linesGroupFocusId}>
            {renderLines()}
          </Element>
        </ScrollContainer>
      </SearchContainer>
    </PageContainer>
  )
}

export const Search = memo(SearchRow)
